import React from 'react';
import { Wifi, WifiOff, BatteryCharging, Battery, BatteryLow, type LucideIcon } from 'lucide-react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps, SessionAvatar } from '../../../types/second-screen.types';
import { formatSlotRole, isCameraRole, isGlassesRole, type DeviceInfo } from '../../../services/n8n';

interface DeviceCardProps {
  device: DeviceInfo;
  avatar: SessionAvatar | null | undefined;
  online: boolean;
  speaking: boolean;
  battIcon: LucideIcon;
  battColor: string;
  battBarColor: string;
  cardClasses: string;
  isDark: boolean;
}

const DeviceCard: React.FC<DeviceCardProps> = ({
  device,
  avatar,
  online,
  speaking,
  battIcon: BattIcon,
  battColor,
  battBarColor,
  cardClasses,
  isDark
}) => {
  const renderAvatar = () => (
    <div className="relative shrink-0">
      {avatar?.imageUrl ? (
        <img
          src={avatar.imageUrl}
          alt={avatar.name}
          className={`w-8 h-8 rounded-full object-cover border ${speaking ? 'border-primary ring-2 ring-primary/30' : 'border-white/10'}`}
        />
      ) : (
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          isDark ? 'bg-white/10' : 'bg-slate-200'
        }`}>
          <span className="text-xs font-bold">
            {avatar?.emoji || device.deviceId.slice(-2).toUpperCase()}
          </span>
        </div>
      )}
      {speaking && (
        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-primary animate-pulse" />
      )}
    </div>
  );

  const renderStatusIcon = () =>
    online ? (
      <Wifi size={10} className="text-green-400 shrink-0" />
    ) : (
      <WifiOff size={10} className="text-red-400 shrink-0" />
    );

  const renderName = () => (
    <span className={`text-xs font-semibold truncate ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
      {avatar?.name || (device.assignedRole && (isCameraRole(device.assignedRole) || isGlassesRole(device.assignedRole)) ? formatSlotRole(device.assignedRole) : device.assignedRole) || device.displayName || device.deviceId}
    </span>
  );

  const renderBatteryOrSpeaking = () =>
    speaking ? (
      <div className="flex items-center gap-1.5 mt-1">
        <span className={`text-xs font-bold ${isDark ? 'text-primary' : 'text-primary-dark'}`}>Speaking...</span>
      </div>
    ) : (
      <div className="flex items-center gap-1.5 mt-1">
        <BattIcon size={10} className={battColor} />
        <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-slate-200'}`}>
          <div
            className={`h-full rounded-full transition-all duration-500 ${battBarColor}`}
            style={{ width: `${device.batteryLevel}%` }}
          />
        </div>
        <span className={`text-xs font-mono ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
          {device.batteryLevel}%
        </span>
      </div>
    );

  return (
    <div
      className={`flex items-center gap-2.5 p-2.5 rounded-lg border ${cardClasses}`}
    >
      {renderAvatar()}

      <div className="min-w-0 flex-1">
        {/* Name + online status */}
        <div className="flex items-center gap-1.5">
          {renderStatusIcon()}
          {renderName()}
        </div>

        {/* State indicator or battery bar */}
        {renderBatteryOrSpeaking()}
      </div>
    </div>
  );
};

const DeviceStatusWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';

  const devices = state?.sessionDevices ?? [];
  const sessionAvatars = state?.sessionAvatars ?? [];

  if (!state || devices.length === 0) {
    return (
      <div className={`flex items-center justify-center h-full text-sm ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
        No devices connected
      </div>
    );
  }

  const getAvatar = (role: string | null) => {
    if (!role) return null;
    return sessionAvatars.find(a => a.role === role);
  };

  const getBatteryIcon = (level: number, charging: boolean) => {
    if (charging) return BatteryCharging;
    if (level < 20) return BatteryLow;
    return Battery;
  };

  const getBatteryColor = (level: number) => {
    if (level < 20) return 'text-red-400';
    if (level < 50) return 'text-amber-400';
    return 'text-green-400';
  };

  const getBatteryBarColor = (level: number) => {
    if (level < 20) return 'bg-red-400';
    if (level < 50) return 'bg-amber-400';
    return 'bg-green-400';
  };

  const getCardClasses = (speaking: boolean) => {
    if (speaking) return isDark ? 'bg-primary/10 border-primary/30' : 'bg-primary/5 border-primary/20';
    return isDark ? 'bg-white/[0.03] border-white/5' : 'bg-slate-50 border-slate-100';
  };

  const isOnline = (lastHeartbeat: string) => {
    if (!lastHeartbeat) return false;
    const diff = Date.now() - new Date(lastHeartbeat).getTime();
    return diff < 30000; // 30 seconds
  };

  const isSpeaking = (device: any) => device.state === 'speaking';

  return (
    <div className="grid grid-cols-2 gap-2 h-full p-3 overflow-y-auto content-start">
      {devices.map(device => {
        const avatar = getAvatar(device.assignedRole);
        const online = isOnline(device.lastHeartbeat);
        const battIcon = getBatteryIcon(device.batteryLevel, device.isCharging);
        const battColor = getBatteryColor(device.batteryLevel);
        const battBarColor = getBatteryBarColor(device.batteryLevel);
        const speaking = isSpeaking(device);
        const cardClasses = getCardClasses(speaking);

        return (
          <DeviceCard
            key={device.deviceId}
            device={device}
            avatar={avatar}
            online={online}
            speaking={speaking}
            battIcon={battIcon}
            battColor={battColor}
            battBarColor={battBarColor}
            cardClasses={cardClasses}
            isDark={isDark}
          />
        );
      })}
    </div>
  );
};

export default DeviceStatusWidget;
